import React, { FC, useMemo, memo } from 'react'
import classNames from 'classnames'
import moment from 'moment-timezone'
import { Tooltip } from 'transfix-ui/components/Tooltip'
import { makeStyles, Box, Typography } from 'transfix-ui/core'
import { IIssueAutomationRuleActionEnum } from 'types/graphqlTypes'
import ActionsMenu from 'apps/marketplace/components/ActionsMenu'
import { EditIcon, DeleteIcon, TitleIcon } from 'transfix-icons'
import { TableRow, TableCell, brandColors } from 'transfix-ui'
import { useUserDetails } from 'utils/hooks'
import { FORMATS } from 'constants/datetime'
import { DISPLAY } from 'constants/display'
import { IIssueAutomationRuleFragment } from '../graphql/IssueAutomationRuleFragment'
import { getRuleDescription } from '../utils'

const useStyles = makeStyles(() => ({
  name: {
    color: brandColors.skyBlue6,
    cursor: 'pointer',
    display: 'inline-block',
  },
  activeRow: {
    backgroundColor: brandColors.coolGray1,
  },
  menuCell: {
    '& > div': {
      float: 'right',
    },
  },
}))

interface IIssueRuleRowProps {
  rule: IIssueAutomationRuleFragment
  onRename: (ruleId: string) => void
  onDelete: (ruleId: string) => void
  onUpdate: (ruleId: string) => void
  selectedRuleId: string | null
}

const IssueRuleRow: FC<IIssueRuleRowProps> = memo(
  ({ onDelete, onRename, onUpdate, rule, selectedRuleId }) => {
    const classes = useStyles()
    const { id, name, ruleAction, updatedAt, updatedBy } = rule || {}
    const ruleType =
      ruleAction === IIssueAutomationRuleActionEnum.create ? 'Open Issue' : 'Close Issue'
    const formattedUpdatedAt = moment(updatedAt).format(FORMATS.monthDayYearTimeFormat24h)
    const { userPermissions } = useUserDetails()
    const hasEditPermission = userPermissions?.canEditIssueAutomation

    const overflowOptions = useMemo(
      () => [
        {
          itemComponent: (
            <Box display='flex'>
              <EditIcon size='large' />
              <Box ml={2} component='span'>
                Edit Rule
              </Box>
            </Box>
          ),
          label: 'edit',
          action: () => onUpdate(id),
        },
        ...(hasEditPermission
          ? [
              {
                itemComponent: (
                  <Box display='flex'>
                    <TitleIcon size='large' />
                    <Box ml={2} component='span'>
                      Rename
                    </Box>
                  </Box>
                ),
                label: 'rename',
                action: () => onRename(id),
              },
              {
                itemComponent: (
                  <Box display='flex'>
                    <DeleteIcon size='large' />
                    <Box ml={2} component='span'>
                      Delete Rule
                    </Box>
                  </Box>
                ),
                label: 'delete',
                action: () => onDelete(id),
              },
            ]
          : []),
      ],
      []
    )

    return (
      <TableRow
        dataTest={`rule-${id}`}
        highlightOnHover
        className={classNames({ [classes.activeRow]: id === selectedRuleId })}>
        <TableCell dataTest={`rule-${id}-name`}>
          <Tooltip
            position='right'
            id={`rule-${id}-description`}
            trigger={
              <Box fontWeight={600} onClick={() => onUpdate(id)} className={classes.name}>
                {name || DISPLAY.empty}
              </Box>
            }>
            <>{getRuleDescription(rule)}</>
          </Tooltip>
        </TableCell>
        <TableCell dataTest={`rule-${id}-rule-type`}>{ruleType || DISPLAY.empty}</TableCell>
        <TableCell dataTest={`rule-${id}-last-updated-by`}>
          <Typography variant='body1' component='span' display='block'>
            {updatedBy.fullName || DISPLAY.empty}
          </Typography>
          <Typography color='textSecondary' variant='caption'>
            {formattedUpdatedAt}
          </Typography>
        </TableCell>
        <TableCell dataTest={`rule-${id}-options`} className={classes.menuCell}>
          <ActionsMenu options={overflowOptions} id={`rule-${id}-overflow`} />
        </TableCell>
      </TableRow>
    )
  }
)

export default IssueRuleRow
